/**
 * AuditIcons.jsx
 *
 * Icons are wrapped in a hard-sized <span> so global `svg { width: 100% }` 
 * rules can NEVER expand them. The SVG itself never has overflow:visible.
 */

/* ── Icon wrapper — always call this ── */
const Wrap = ({ size, children }) => (
  <span
    style={{
      display:    "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      width:    size,
      height:   size,
      minWidth: size,
      minHeight: size,
      flexShrink: 0,
      lineHeight: 1,
      verticalAlign: "middle",
    }}
  >
    {children}
  </span>
);

export const AuditIcon = ({ name, size = 14, color = "currentColor" }) => {
  const s = Number(size);
  const svg = {
    width:          s,
    height:         s,
    viewBox:        "0 0 24 24",
    fill:           "none",
    stroke:         color,
    strokeWidth:    2,
    strokeLinecap:  "round",
    strokeLinejoin: "round",
    style:          { display: "block", flexShrink: 0 },
  };

  const icon = (() => {
    if (name === "location") return (
      <svg {...svg}>
        <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"/>
        <circle cx="12" cy="9" r="2.5"/>
      </svg>
    );
    if (name === "search") return (
      <svg {...svg}>
        <circle cx="11" cy="11" r="8"/>
        <path d="m21 21-4.35-4.35"/>
      </svg>
    );
    if (name === "chevron") return (
      <svg {...svg}>
        <path d="M6 9l6 6 6-6"/>
      </svg>
    );
    if (name === "check") return (
      <svg {...svg}>
        <polyline points="20 6 9 17 4 12"/>
      </svg>
    );
    if (name === "x") return (
      <svg {...svg}>
        <line x1="18" y1="6" x2="6" y2="18"/>
        <line x1="6" y1="6" x2="18" y2="18"/>
      </svg>
    );
    if (name === "filter") return (
      <svg {...svg}>
        <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/>
      </svg>
    );
    if (name === "refresh") return (
      <svg {...svg}>
        <polyline points="23 4 23 10 17 10"/>
        <polyline points="1 20 1 14 7 14"/>
        <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
      </svg>
    );
    if (name === "file") return (
      <svg {...svg}>
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
        <polyline points="14 2 14 8 20 8"/>
      </svg>
    );
    if (name === "user") return (
      <svg {...svg}>
        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
        <circle cx="12" cy="7" r="4"/>
      </svg>
    );
    if (name === "calendar") return (
      <svg {...svg}>
        <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
        <line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/>
        <line x1="3" y1="10" x2="21" y2="10"/>
      </svg>
    );
    if (name === "arrow-left") return (
      <svg {...svg}>
        <line x1="19" y1="12" x2="5" y2="12"/>
        <polyline points="12 19 5 12 12 5"/>
      </svg>
    );
    return <svg {...svg}/>;
  })();

  return <Wrap size={s}>{icon}</Wrap>;
};

/* ── HighlightText ── */
export const HighlightText = ({ text = "", query = "" }) => {
  if (!query || !text) return <>{text}</>;
  const idx = text.toLowerCase().indexOf(query.toLowerCase());
  if (idx === -1) return <>{text}</>;
  return (
    <>
      {text.slice(0, idx)}
      <mark style={{ background: "var(--accent-3)", color: "var(--accent)", borderRadius: 2, padding: "0 1px", fontWeight: 800 }}>
        {text.slice(idx, idx + query.length)}
      </mark>
      {text.slice(idx + query.length)}
    </>
  );
};

/* ── StatusBadge ── */
const STATUS_MAP = {
  PENDING:   { label: "Pending",   bg: "#fef3c7", color: "#b45309", border: "#fde68a" },
  IN_REVIEW: { label: "In Review", bg: "#eff6ff", color: "#2563eb", border: "#bfdbfe" },
  APPROVED:  { label: "Approved",  bg: "#dcfce7", color: "#15803d", border: "#bbf7d0" },
  ACCEPTED:  { label: "Accepted",  bg: "#dcfce7", color: "#15803d", border: "#bbf7d0" },
  REJECTED:  { label: "Rejected",  bg: "#fee2e2", color: "#dc2626", border: "#fecaca" },
};

export const StatusBadge = ({ status, size = "md" }) => {
  const s   = STATUS_MAP[status] || { label: status || "—", bg: "#f1f5f9", color: "#64748b", border: "#e2e8f0" };
  const pad = size === "sm" ? "1px 7px" : "3px 10px";
  const fs  = size === "sm" ? 9.5 : 10.5;
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 5,
      padding: pad, borderRadius: 20, fontSize: fs, fontWeight: 700,
      background: s.bg, color: s.color, border: `1.5px solid ${s.border}`,
      whiteSpace: "nowrap", letterSpacing: "0.03em", textTransform: "uppercase",
    }}>
      <span style={{ width: 5, height: 5, borderRadius: "50%", background: s.color, flexShrink: 0 }}/>
      {s.label}
    </span>
  );
};